import { Component, Input, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { TooltipModule } from 'primeng/tooltip'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faRoute } from '@fortawesome/free-solid-svg-icons'
import { DateTools } from '../../../core/tools/date-tools'

export interface RouteInfo {
  distance_km: number
  duration_min: number
}

export interface RouteDepartureInfo {
  departureTime: Date
  firstEventTime: Date
  travelMin: number
  isTight: boolean
}

export interface RouteReturnInfo {
  returnTime: Date
  lastEventEndTime?: Date
  affairEndTime?: Date
  travelMin: number
}

export interface PreSelectedSlotData {
  slot?: {
    start: string
    end: string
  }
  travelInfo?: {
    isFirstSlotOfDay?: boolean
    homeDepartureTime?: Date | string
    homeDepartureIsTight?: boolean
    travelFromHome?: number
    requiresShift?: boolean
    shiftedEventId?: number | null
    shiftedEventTitle?: string | null
  }
}

@Component({
  selector: 'app-daily-info-panel',
  standalone: true,
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    TooltipModule,
    FontAwesomeModule,
    DatePipe,
    DecimalPipe,
  ],
  templateUrl: './daily-info-panel.component.html',
  styleUrls: ['./daily-info-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailyInfoPanelComponent {
  // Icons
  faRoute = faRoute

  // Required inputs
  @Input() selectedDate: Date | string | null = null
  @Input() dailyCalendarEvents: any[] = []
  @Input() dailyRouteInfo: RouteInfo | null = null
  @Input() projectedRouteInfo: RouteInfo | null = null
  @Input() loadingDailyRoute: boolean = false
  @Input() preSelectedSlot: PreSelectedSlotData | null = null

  // New item info (affair or intervention)
  @Input() newItemName: string = ''
  @Input() newItemCityWithPostalCode: string = ''
  @Input() labelNewItem: string = 'intervention' // "affaire" or "intervention"

  // Technician info
  @Input() technicianColor: string = '#ff6b35'
  @Input() technicianHomeCity: string = ''
  @Input() hasHomeCoordinates: boolean = false

  // Location type info (home or hotel)
  @Input() startLocationType: 'home' | 'hotel' = 'home'
  @Input() endLocationType: 'home' | 'hotel' = 'home'
  @Input() hasStartLocationCoordinates: boolean = false
  @Input() hasEndLocationCoordinates: boolean = false
  @Input() startLocationCity: string = ''
  @Input() endLocationCity: string = ''
  // Journée d'hôtel sans coordonnées : trajet non calculé mais "à prendre en compte"
  @Input() startTravelUncounted: boolean = false
  @Input() endTravelUncounted: boolean = false

  // Route timing info
  @Input() currentRouteDeparture: RouteDepartureInfo | null = null
  @Input() currentRouteReturn: RouteReturnInfo | null = null
  @Input() projectedRouteReturn: RouteReturnInfo | null = null

  // Google Maps URLs (optional, for links)
  @Input() googleMapsUrlCurrentRoute: string | null = null
  @Input() googleMapsUrlWithNewItem: string | null = null

  /**
   * Get display label text with proper formatting
   */
  get labelWithArticle(): string {
    if (this.labelNewItem === 'affaire') {
      return 'affaire'
    }
    return 'intervention'
  }

  /**
   * Get start location label ("Domicile" or "Hôtel")
   */
  get startLocationLabel(): string {
    return this.startLocationType === 'hotel' ? 'Hôtel' : 'Domicile'
  }

  /**
   * Get end location label ("Domicile" or "Hôtel")
   */
  get endLocationLabel(): string {
    return this.endLocationType === 'hotel' ? 'Hôtel' : 'Domicile'
  }

  /**
   * Get start location emoji
   */
  get startLocationEmoji(): string {
    return this.startLocationType === 'hotel' ? '🏨' : '🏠'
  }

  /**
   * Get end location emoji
   */
  get endLocationEmoji(): string {
    return this.endLocationType === 'hotel' ? '🏨' : '🏠'
  }

  /**
   * Whether the start location row should be shown
   */
  get showStartLocation(): boolean {
    // Journée d'hôtel sans coordonnées : on affiche quand même la ligne pour
    // signaler que le trajet du matin est "à prendre en compte".
    if (this.startTravelUncounted) return true
    return this.startLocationType === 'hotel'
      ? this.hasStartLocationCoordinates
      : this.hasHomeCoordinates
  }

  /**
   * Whether the end location row should be shown
   */
  get showEndLocation(): boolean {
    // Journée d'hôtel sans coordonnées : on affiche quand même la ligne pour
    // signaler que le trajet du soir est "à prendre en compte".
    if (this.endTravelUncounted) return true
    return this.endLocationType === 'hotel'
      ? this.hasEndLocationCoordinates
      : this.hasHomeCoordinates
  }

  /**
   * Get "Avec affaire:" or "Avec intervention:" text
   */
  get withLabel(): string {
    if (this.labelNewItem === 'affaire') {
      return 'Avec affaire:'
    }
    return 'Avec intervention:'
  }

  /**
   * Get "Trajet affaire:" or "Trajet intervention:" text
   */
  get tripLabel(): string {
    if (this.labelNewItem === 'affaire') {
      return 'Trajet affaire:'
    }
    return 'Trajet intervention:'
  }

  /**
   * Format duration in minutes to human-readable format
   */
  formatDuration(minutes: number): string {
    return DateTools.formatDuration(minutes)
  }

  /**
   * Format the home departure time from preSelectedSlot
   */
  formatHomeDepartureTime(): string {
    const travelInfo = this.preSelectedSlot?.travelInfo
    if (!travelInfo?.homeDepartureTime) return ''

    if (travelInfo.homeDepartureTime instanceof Date) {
      return travelInfo.homeDepartureTime.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const date = new Date(travelInfo.homeDepartureTime)
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Format the current route departure time from home
   */
  formatCurrentRouteDepartureTime(): string {
    if (!this.currentRouteDeparture?.departureTime) return ''

    return this.currentRouteDeparture.departureTime.toLocaleTimeString(
      'fr-FR',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    )
  }

  /**
   * Format event time
   */
  formatEventTime(event: any): string {
    return DateTools.formatEventTime(event)
  }

  /**
   * Format the pre-selected slot start time
   */
  formatPreSelectedSlotTime(): string {
    if (!this.preSelectedSlot?.slot?.start) return ''

    const startDate = new Date(this.preSelectedSlot.slot.start)
    return startDate.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Format the pre-selected slot end time
   */
  formatPreSelectedSlotEndTime(): string {
    if (!this.preSelectedSlot?.slot?.end) return ''

    const endDate = new Date(this.preSelectedSlot.slot.end)
    return endDate.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Format the projected return time to home
   */
  formatProjectedReturnTime(): string {
    if (!this.projectedRouteReturn?.returnTime) return ''

    return this.projectedRouteReturn.returnTime.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Format the current route return time
   */
  formatCurrentRouteReturnTime(): string {
    if (!this.currentRouteReturn?.returnTime) return ''

    return this.currentRouteReturn.returnTime.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Check if an event is an HOTEL event (overnight stay)
   */
  isHotelEvent(event: any): boolean {
    return (
      event.data?.eventType?.name === 'HOTEL' || event.eventTypeName === 'HOTEL'
    )
  }

  /**
   * Get sorted daily events by start time, excluding HOTEL events
   * (HOTEL events are represented by the start/end location rows)
   */
  getSortedDailyEvents(): any[] {
    if (!this.dailyCalendarEvents?.length) return []

    return [...this.dailyCalendarEvents]
      .filter((e) => !this.isHotelEvent(e))
      .sort((a, b) => {
        const aStart = new Date(a.start || a.startDate).getTime()
        const bStart = new Date(b.start || b.startDate).getTime()
        return aStart - bStart
      })
  }

  /**
   * Check if new item should be inserted before a given event
   */
  shouldInsertNewItemBefore(event: any, isFirst: boolean): boolean {
    if (!this.preSelectedSlot?.slot?.start) return false

    const slotStart = new Date(this.preSelectedSlot.slot.start)
    const eventStart = new Date(event.start || event.startDate)

    // Slot must be before this event
    if (slotStart >= eventStart) return false

    const sortedEvents = this.getSortedDailyEvents()
    const eventIndex = sortedEvents.indexOf(event)

    // If this is the first event, insert before it
    if (eventIndex === 0) return true

    // Otherwise, only insert here if the slot comes after the previous event
    // (meaning this is the correct chronological position)
    const prevEventStart = new Date(
      sortedEvents[eventIndex - 1].start ||
        sortedEvents[eventIndex - 1].startDate
    )
    return slotStart >= prevEventStart
  }

  /**
   * Check if new item should be inserted at the end (after all events)
   */
  shouldInsertNewItemAtEnd(): boolean {
    if (!this.preSelectedSlot?.slot?.start) return false

    const sortedEvents = this.getSortedDailyEvents()
    if (sortedEvents.length === 0) return true

    const slotStart = new Date(this.preSelectedSlot.slot.start)

    // Check if slot comes after all existing events
    for (const event of sortedEvents) {
      const eventStart = new Date(event.start || event.startDate)
      if (slotStart < eventStart) {
        return false // Slot is before at least one event
      }
    }
    return true
  }

  /**
   * Get the return info to display (projected if new item is at end, otherwise current)
   */
  getReturnInfoToDisplay(): {
    returnTime: string
    travelMin: number
    isProjected: boolean
  } | null {
    // If new item is pre-selected and would be at end, show projected return
    if (
      this.preSelectedSlot &&
      this.shouldInsertNewItemAtEnd() &&
      this.projectedRouteReturn
    ) {
      return {
        returnTime: this.formatProjectedReturnTime(),
        travelMin: this.projectedRouteReturn.travelMin,
        isProjected: true,
      }
    }
    // Otherwise show current route return
    if (this.currentRouteReturn) {
      return {
        returnTime: this.formatCurrentRouteReturnTime(),
        travelMin: this.currentRouteReturn.travelMin,
        isProjected: false,
      }
    }
    return null
  }

  /**
   * Check if an event has valid GPS coordinates
   */
  hasValidCoordinatesForEvent(event: any): boolean {
    const lat =
      event.data?.workSiteLatitude ||
      event.workSiteLatitude ||
      event.data?.workSiteLat ||
      event.workSiteLat ||
      event.data?.latitude ||
      event.latitude
    const lng =
      event.data?.workSiteLongitude ||
      event.workSiteLongitude ||
      event.data?.workSiteLng ||
      event.workSiteLng ||
      event.data?.longitude ||
      event.longitude
    return !!(lat && lng && +lat !== 0 && +lng !== 0)
  }

  /**
   * Check if an event is displayed on the map (has valid coordinates).
   * Events without coordinates are not on the route and should not get a route number.
   */
  isOnMap(event: any): boolean {
    return this.hasValidCoordinatesForEvent(event)
  }

  /**
   * Get the route order number for a given event index.
   * Only counts events that are on the map (have valid coordinates).
   */
  getRouteOrder(eventIndex: number): number | null {
    const sorted = this.getSortedDailyEvents()
    let order = 0
    for (let i = 0; i <= eventIndex && i < sorted.length; i++) {
      if (this.isOnMap(sorted[i])) {
        order++
      }
    }
    return this.isOnMap(sorted[eventIndex]) ? order : null
  }

  /**
   * Check if a calendar item is an event (absence, congé, etc.) vs an intervention
   */
  isCalendarEvent(event: any): boolean {
    return event.data?.resource === 'event'
  }

  /**
   * Get display label for a calendar item
   */
  getItemLabel(event: any): string {
    if (this.isCalendarEvent(event)) {
      return (
        event.title ||
        event.data?.name ||
        event.data?.eventType?.name ||
        'Événement'
      )
    }
    return event.title || event.name || 'Intervention'
  }

  /**
   * Count of interventions only (excluding HOTEL events)
   */
  get interventionCount(): number {
    return this.dailyCalendarEvents.filter(
      (e) => !this.isCalendarEvent(e) && !this.isHotelEvent(e)
    ).length
  }

  /**
   * Count of events (absences, etc.) only (excluding HOTEL events)
   */
  get calendarEventCount(): number {
    return this.dailyCalendarEvents.filter(
      (e) => this.isCalendarEvent(e) && !this.isHotelEvent(e)
    ).length
  }

  /**
   * Get city with postal code for an event
   */
  getEventCityWithPostalCode(event: any): string {
    const postalCode =
      event.data?.workSitePostalCode ||
      event.workSitePostalCode ||
      event.data?.postalCode ||
      event.postalCode ||
      ''
    const locality =
      event.data?.workSiteLocality ||
      event.workSiteLocality ||
      event.data?.workSiteCity ||
      event.workSiteCity ||
      event.data?.locality ||
      event.locality ||
      ''

    if (postalCode && locality) {
      return `${postalCode} ${locality}`
    }
    return locality || postalCode || ''
  }

  /**
   * Check if this is a shift-slot selection (requires moving an existing intervention)
   */
  get isShiftSlotSelected(): boolean {
    return this.preSelectedSlot?.travelInfo?.requiresShift === true
  }

  /**
   * Check if a given event is the one that would be shifted by the current shift-slot selection
   */
  isShiftedEvent(event: any): boolean {
    if (!this.isShiftSlotSelected) return false
    const shiftedId = this.preSelectedSlot?.travelInfo?.shiftedEventId
    if (shiftedId == null) return false

    const eventId = event.data?.id ?? event.id
    return eventId != null && String(eventId) === String(shiftedId)
  }

  /**
   * Build the projected schedule for shift-slot mode.
   * Returns an ordered list of items: existing events (excluding shifted one)
   * + the new affair inserted at the right position
   * + the shifted event as last item with type 'shiftedEvent'.
   * Each item has: type ('event' | 'newItem' | 'shiftedEvent'), order (1-based), event? data
   */
  getProjectedSchedule(): {
    type: 'event' | 'newItem' | 'shiftedEvent'
    order: number
    event?: any
  }[] {
    const sortedEvents = this.getSortedDailyEvents()
    const items: {
      type: 'event' | 'newItem' | 'shiftedEvent'
      order: number
      event?: any
    }[] = []

    // Separate shifted event from the rest
    const shiftedEvent = sortedEvents.find((e) => this.isShiftedEvent(e))
    const filteredEvents = sortedEvents.filter((e) => !this.isShiftedEvent(e))

    // Insert the new affair at the right position among remaining events
    const slotStart = this.preSelectedSlot?.slot?.start
      ? new Date(this.preSelectedSlot.slot.start).getTime()
      : 0

    let newItemInserted = false
    let order = 1

    for (const event of filteredEvents) {
      const eventStart = new Date(event.start || event.startDate).getTime()

      // Insert new item before this event if its time is earlier
      if (!newItemInserted && slotStart < eventStart) {
        items.push({ type: 'newItem', order: order++ })
        newItemInserted = true
      }

      // Only increment order for events on the map
      const eventOrder = this.isOnMap(event) ? order++ : 0
      items.push({ type: 'event', order: eventOrder, event })
    }

    // If not yet inserted, add at the end
    if (!newItemInserted) {
      items.push({ type: 'newItem', order: order++ })
    }

    // Add shifted event as last numbered item (only if on map)
    if (shiftedEvent) {
      const shiftedOrder = this.isOnMap(shiftedEvent) ? order++ : 0
      items.push({
        type: 'shiftedEvent',
        order: shiftedOrder,
        event: shiftedEvent,
      })
    }

    return items
  }

  /**
   * Get the shifted event data (the one being moved out)
   */
  getShiftedEvent(): any | null {
    if (!this.isShiftSlotSelected) return null
    return (
      this.getSortedDailyEvents().find((e) => this.isShiftedEvent(e)) ?? null
    )
  }
}
